import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Option } from '@rivethealth/data/options';
import { ObservableInput } from 'observable-input';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Loadable } from '../helpers/loadable';
import { DeprecatedSingleSelectDropdownComponent } from './single-select-dropdown.component';
import { SingleSelectNodeDirective } from './single-select-node.directive';

export enum ValueType {
  ID = 0,
  Option = 1,
}

/**
 * @deprecated Use riv-single-select instead.
 * @see https://rivethealth.github.io/design-system/?path=/story/lib-input-single-select--example
 */
@Component({
  selector: 'deprecated-single-select-dropdown-field',
  templateUrl: './single-select-dropdown-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(
        () => DeprecatedSingleSelectDropdownFieldComponent,
      ),
    },
  ],
})
export class DeprecatedSingleSelectDropdownFieldComponent<T = unknown> {
  @Input() label: string;
  @Input() help: string;
  @Input() required: boolean = false;
  @Input() placeholder: string;
  @Input() disabled: boolean = false;
  @Input() autoFocus: boolean = false;
  @Input() exact: boolean = false;
  @Input() filterable: boolean = false;
  @Input() error = false;
  @Input() warning = false;
  @Input() loadable: Loadable<any>;
  @Input() options: Option[] | string[];
  @Output() valueChange = new EventEmitter();

  @Input() actionText: string;
  @Output() action = new EventEmitter();

  @ObservableInput()
  @Input()
  optionsLoadable!: Observable<Loadable<{ options: Option[]; count: number }>>;

  @ObservableInput()
  @Input('dismiss')
  readonly dismiss!: Observable<number>;

  @Output() readonly filter = new Subject<string>();

  @ViewChild(DeprecatedSingleSelectDropdownComponent, { static: false })
  dropdownElement: DeprecatedSingleSelectDropdownComponent;

  @ContentChild(SingleSelectNodeDirective, { static: false })
  nodeDirective: SingleSelectNodeDirective<T>;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  @Input()
  private value: string | Option | null;

  get idValue(): string | null | undefined {
    if (this.valueType === ValueType.ID) {
      return (this.value as string) || null;
    }
    return undefined;
  }

  idValueChange = new Subject<string>();

  idValueChangeSub = this.idValueChange.pipe(
    map(newId => {
      if (this.valueType === ValueType.ID) {
        this.valueChange.emit(newId);
        this.value = newId;
        this.propagateChange(newId);
      }
    }),
  );

  get optionValue(): Option | null | undefined {
    if (this.valueType === ValueType.Option) {
      return (this.value as Option) || null;
    }
    return undefined;
  }

  optionValueChange = new Subject<Option>();

  optionValueChangeSub = this.optionValueChange.pipe(
    map(newOption => {
      if (this.valueType === ValueType.Option) {
        this.valueChange.emit(newOption);
        this.value = newOption;
        this.propagateChange(newOption);
      }
    }),
  );

  @Input()
  valueType: ValueType = ValueType.ID;

  private propagateChange = (_: any) => {};

  writeValue(value: any) {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (_: any) => {}) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  public close() {
    this.dropdownElement.close();
  }

  public open() {
    this.dropdownElement.open();
  }

  public focus() {
    // this.dropdownElement.focus();
  }

  ValueType = ValueType;
}
